import { CashflowChart, type CashflowPoint } from "@/components/accounting/cashflow-chart";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api } from "@/lib/api";
import { cn, formatCurrency } from "@/lib/utils";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";

/**
 * `/accounting` — the morning screen.
 *
 * It answers four questions and nothing else: how the year is going,
 * who owes us, whom we owe, and what is waiting for a person. Every
 * figure is a link to the rows it was computed from — the point of a
 * dashboard is to get out of it quickly, into the list that explains
 * the number.
 *
 * The migration's own verdict (is Fakturoid safe to switch off?) used
 * to live here and now sits on `/accounting/cutover`: it is a question
 * asked once, not every morning.
 */

type Overview = {
  asOf: string;
  months: CashflowPoint[];
  issued: { unpaidCount: number; unpaid: string; overdueCount: number; overdue: string };
  received: { unpaidCount: number; unpaid: string; overdueCount: number; overdue: string };
  bank: { month: string; incoming: string; outgoing: string; lastMovementOn: string | null };
  cash: { balance: string; registers: number };
  alerts: {
    unmatchedPayments: number;
    scansWaiting: number;
    withoutArchivedPdf: number;
    overdueIssued: number;
  };
  latest: Array<{
    id: string;
    number: string;
    type: string;
    status: string;
    partnerName: string;
    issuedOn: string;
    total: string;
    currency: string;
  }>;
};

const statusVariant: Record<string, "success" | "warning" | "secondary" | "outline"> = {
  paid: "success",
  partially_paid: "warning",
  issued: "outline",
  draft: "secondary",
  cancelled: "secondary",
};

export const Route = createFileRoute("/_app/accounting/")({
  loader: async () => {
    try {
      return await api.get<Overview>("/api/accounting/overview?months=12");
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: DashboardPage,
});

function DashboardPage() {
  const data = Route.useLoaderData();
  const year = data.months.reduce(
    (sum, month) => ({
      income: sum.income + Number(month.income),
      expense: sum.expense + Number(month.expense),
    }),
    { income: 0, expense: 0 },
  );
  const profit = year.income - year.expense;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">Overview</h1>
          <p className="text-muted-foreground text-sm">
            The last twelve months by issue date, and what is still open on either side.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/accounting/cutover">Leaving Fakturoid</Link>
          </Button>
          <Button asChild>
            <Link to="/billing">New invoice from a report</Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-baseline justify-between gap-4">
          <CardTitle>Income and expenses</CardTitle>
          <div className="flex gap-6 text-sm">
            <Legend label="Income" value={year.income} className="text-emerald-600" />
            <Legend label="Expenses" value={year.expense} className="text-red-600" />
            <Legend
              label="Difference"
              value={profit}
              className={profit < 0 ? "text-red-600" : "text-amber-600"}
            />
          </div>
        </CardHeader>
        <CardContent>
          <CashflowChart months={data.months} />
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <OpenCard
          title="Invoices"
          allHref="/accounting/invoices"
          side="invoices"
          totals={data.issued}
        />
        <OpenCard
          title="Expenses"
          allHref="/accounting/expenses"
          side="expenses"
          totals={data.received}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Bank this month</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row label="In" value={formatCurrency(Number(data.bank.incoming))} tone="ok" />
            <Row label="Out" value={formatCurrency(Number(data.bank.outgoing))} tone="danger" />
            <p className="pt-1 text-muted-foreground text-xs">
              Last movement:{" "}
              {data.bank.lastMovementOn ? (
                <LocalDate value={data.bank.lastMovementOn} />
              ) : (
                "nothing imported yet"
              )}
            </p>
            <Link to="/accounting/bank" className="inline-block pt-1 text-sm underline">
              Bank
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Cash</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div className="font-semibold text-xl tabular-nums">
              {formatCurrency(Number(data.cash.balance))}
            </div>
            <p className="text-muted-foreground text-xs">
              {data.cash.registers} register{data.cash.registers === 1 ? "" : "s"}, balance from the
              documents
            </p>
            <Link to="/accounting/cash" className="inline-block pt-1 text-sm underline">
              Cash book
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Waiting for you</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {data.alerts.unmatchedPayments > 0 && (
              <Link to="/accounting/bank" className="flex items-center gap-2 hover:underline">
                <Badge variant="warning">{data.alerts.unmatchedPayments}</Badge>
                <span>unmatched payments</span>
              </Link>
            )}
            {data.alerts.scansWaiting > 0 && (
              <Link to="/accounting/inbox" className="flex items-center gap-2 hover:underline">
                <Badge variant="warning">{data.alerts.scansWaiting}</Badge>
                <span>documents in the inbox</span>
              </Link>
            )}
            {data.alerts.overdueIssued > 0 && (
              <Link
                to="/accounting/invoices"
                search={{ filter: "overdue" as const }}
                className="flex items-center gap-2 hover:underline"
              >
                <Badge variant="warning">{data.alerts.overdueIssued}</Badge>
                <span>invoices overdue</span>
              </Link>
            )}
            {data.alerts.withoutArchivedPdf > 0 && (
              <Link to="/accounting/cutover" className="flex items-center gap-2 hover:underline">
                <Badge variant="warning">{data.alerts.withoutArchivedPdf}</Badge>
                <span>imported documents without a PDF</span>
              </Link>
            )}
            {data.alerts.unmatchedPayments +
              data.alerts.scansWaiting +
              data.alerts.overdueIssued +
              data.alerts.withoutArchivedPdf ===
              0 && <p className="text-muted-foreground">Nothing — the books are tidy.</p>}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Latest invoices</CardTitle>
          <Link to="/accounting/invoices" className="text-muted-foreground text-sm underline">
            See all
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          {data.latest.length === 0 ? (
            <p className="p-6 text-muted-foreground text-sm">Nothing issued yet.</p>
          ) : (
            <ul className="divide-y">
              {data.latest.map((doc) => (
                <li key={doc.id}>
                  <Link
                    to="/accounting/invoices/$id"
                    params={{ id: doc.id }}
                    className="flex flex-wrap items-center gap-3 px-6 py-3 hover:bg-muted/50"
                  >
                    <span className="w-24 font-mono text-sm">{doc.number}</span>
                    <Badge variant={statusVariant[doc.status] ?? "outline"} className="text-[10px]">
                      {doc.status.replace("_", " ")}
                    </Badge>
                    <LocalDate value={doc.issuedOn} className="text-muted-foreground text-sm" />
                    <span className="flex-1 truncate text-sm">{doc.partnerName}</span>
                    <span className="tabular-nums">
                      {formatCurrency(Number(doc.total), doc.currency)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Legend({ label, value, className }: { label: string; value: number; className: string }) {
  return (
    <div>
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className={cn("font-semibold tabular-nums", className)}>{formatCurrency(value)}</div>
    </div>
  );
}

function OpenCard({
  title,
  allHref,
  side,
  totals,
}: {
  title: string;
  allHref: "/accounting/invoices" | "/accounting/expenses";
  side: "invoices" | "expenses";
  totals: { unpaidCount: number; unpaid: string; overdueCount: number; overdue: string };
}) {
  const overdue = Number(totals.overdue);
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">{title}</CardTitle>
        <Link to={allHref} className="text-muted-foreground text-sm underline">
          All {side}
        </Link>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-4">
        <Link to={allHref} search={{ filter: "unpaid" as const }} className="block">
          <div className="text-muted-foreground text-xs">Unpaid ({totals.unpaidCount})</div>
          <div className="font-semibold text-xl tabular-nums">
            {formatCurrency(Number(totals.unpaid))}
          </div>
        </Link>
        <Link to={allHref} search={{ filter: "overdue" as const }} className="block">
          <div className="text-muted-foreground text-xs">Overdue ({totals.overdueCount})</div>
          <div
            className={cn(
              "font-semibold text-xl tabular-nums",
              overdue > 0 ? "text-red-600" : "text-muted-foreground",
            )}
          >
            {formatCurrency(overdue)}
          </div>
        </Link>
      </CardContent>
    </Card>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone: "ok" | "danger" }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={cn(
          "font-medium tabular-nums",
          tone === "ok" ? "text-emerald-600" : "text-red-600",
        )}
      >
        {value}
      </span>
    </div>
  );
}
